import React from 'react';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';

// Icon components mapped by key
const ICON_COMPONENTS = {
  VpnKeyOutlinedIcon,
  MedicalServicesOutlinedIcon,
  LocalFireDepartmentOutlinedIcon,
  HealthAndSafetyOutlinedIcon,
} as const;

// Exact name matches for duty icons (highest priority)
const ICON_BY_NAME: Record<string, keyof typeof ICON_COMPONENTS> = {
  'Key Holder': 'VpnKeyOutlinedIcon',
  'First Aid Officer': 'MedicalServicesOutlinedIcon',
  'Fire Warden': 'LocalFireDepartmentOutlinedIcon',
  'Health & Safety Officer': 'HealthAndSafetyOutlinedIcon',
  'Mental Health First Aider': 'MedicalServicesOutlinedIcon',
};

// Pattern-based fallback for icon matching
const ICON_PATTERNS: Array<{ pattern: RegExp; icon: keyof typeof ICON_COMPONENTS }> = [
  { pattern: /key/i, icon: 'VpnKeyOutlinedIcon' },
  { pattern: /(first aid|medical|mental health)/i, icon: 'MedicalServicesOutlinedIcon' },
  { pattern: /fire/i, icon: 'LocalFireDepartmentOutlinedIcon' },
  { pattern: /(health|safety)/i, icon: 'HealthAndSafetyOutlinedIcon' },
];

/**
 * Gets the appropriate icon component for a duty based on its name
 * @param dutyName - The name of the duty
 * @param size - The size of the icon in pixels (default: 24)
 * @returns The icon component or null if no match found
 */
export const getDutyIcon = (dutyName: string, size: number = 24): React.ReactElement | null => {
  // Try exact match first
  if (ICON_BY_NAME[dutyName]) {
    const IconComponent = ICON_COMPONENTS[ICON_BY_NAME[dutyName]];
    return <IconComponent sx={{ fontSize: size }} />;
  }
  
  // Fallback to pattern matching
  const lowerName = dutyName.toLowerCase();
  const match = ICON_PATTERNS.find(({ pattern }) => pattern.test(lowerName));
  
  if (match) {
    const IconComponent = ICON_COMPONENTS[match.icon];
    return <IconComponent sx={{ fontSize: size }} />;
  }
  
  return null;
};

/**
 * Gets the icon component class (not JSX element) for use in other contexts
 * @param dutyName - The name of the duty
 * @returns The icon component class or undefined if no match found
 */
export const getDutyIconComponent = (dutyName: string): React.ComponentType<{ sx?: any }> | undefined => {
  // Try exact match first
  if (ICON_BY_NAME[dutyName]) {
    return ICON_COMPONENTS[ICON_BY_NAME[dutyName]];
  }
  
  // Fallback to pattern matching
  const lowerName = dutyName.toLowerCase();
  const match = ICON_PATTERNS.find(({ pattern }) => pattern.test(lowerName));
  return match ? ICON_COMPONENTS[match.icon] : undefined;
};
